import { zodResolver } from "@hookform/resolvers/zod";
import LoadingButton from "@mui/lab/LoadingButton";
import { Divider, FormControl, Grid, Typography } from "@mui/material";
import { t } from "i18next";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ControlledSelect } from "../../components/form/ControlledSelect/ControlledSelect";
import { ControlledTextField } from "../../components/form/ControlledTextField/ControlledTextField";
import { LeadStatusSelect } from "../../components/form/LeadStatusSelect/LeadStatusSelect";
import { ProductSelector } from "./ProductSelector";

const addLeadFormSchema = z.object({
    personName: z.string().optional().nullable(),
    personSurname: z.string().optional().nullable(),
    personEmail: z.preprocess(
        (val) => (val === "" ? undefined : val),
        z.string().email(t("form.validations.incorrect_email")).optional().nullable(),
    ),
    personPhone: z.string().optional().nullable(),
    leadSource: z.string(),
    leadStatus: z.string().optional(),
    leadInvestment: z.string().optional(),
    leadCaretakerId: z.string().optional(),
    authorComment: z.string().optional(),
    sellerComment: z.string().optional(),
    statusReason: z.string().nullable().optional(),
    statusNote: z.string().nullable().optional(),
    productIds: z.array(z.string()).optional(),
});

export function LeadForm(props) {
    const { handleSubmit, control, watch, setValue, getValues } = useForm({
        resolver: zodResolver(addLeadFormSchema),
        defaultValues: props.defaultValues,
    });

    const requiredLeadFields = watch([
        "leadSource",
        "leadStatus",
    ]).reduce((acc, curr) => acc && Boolean(curr), true);

    const personEmail = watch("personEmail");
    const personPhone = watch("personPhone");

    const submitEnabled = requiredLeadFields && (personEmail || personPhone);

    return (
        <Grid container direction={"column"}>
            <FormControl>
                <Grid item xs={12}>
                    <Typography variant="h6" align={"left"}>
                        {t("leads.form.personalData")}
                    </Typography>
                </Grid>
                <Grid container direction={"row"} rowSpacing={1} columnSpacing={2}>
                    <Grid item xs={6}>
                        <ControlledTextField
                            control={control}
                            name="personName"
                            label={`${t("leads.form.firstName")}`}
                            props={{
                                disabled: props.disablePersonFields,
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <ControlledTextField
                            control={control}
                            name="personEmail"
                            props={{
                                disabled: props.disablePersonFields,
                            }}
                            label={t("leads.form.email")}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <ControlledTextField
                            control={control}
                            name="personSurname"
                            props={{
                                disabled: props.disablePersonFields,
                            }}
                            label={`${t("leads.form.lastName")}`}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <ControlledTextField
                            control={control}
                            name="personPhone"
                            props={{
                                disabled: props.disablePersonFields,
                            }}
                            label={t("leads.form.phone")}
                        />
                    </Grid>
                </Grid>
                <Divider variant="middle" sx={{ m: 2 }} />
                <ProductSelector
                    getFormProductIds={() => getValues().productIds || []}
                    setFormProductIds={(ids) => setValue("productIds", ids)}
                />
                <Divider variant="middle" sx={{ m: 2 }} />
                {props.isEditMode && (
                    <ControlledTextField
                        control={control}
                        name="authorComment"
                        label={`${t("leads.form.authorComment")}`}
                        props={{
                            rows: 3,
                            multiline: true,
                            disabled: true,
                            sx: { marginBottom: 2 },
                            inputProps: { style: { resize: "vertical" } },
                        }}
                    />
                )}
                <ControlledTextField
                    control={control}
                    name="sellerComment"
                    label={`${t("leads.form.sellerComment")}`}
                    props={{
                        rows: 3,
                        multiline: true,
                        sx: { marginBottom: 2 },
                        inputProps: { style: { resize: "vertical" } },
                    }}
                />
                <Grid container direction={"row"} rowSpacing={1} columnSpacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={4}>
                        <ControlledSelect
                            control={control}
                            name="leadSource"
                            label={`${t("leads.form.contactSource")}*`}
                            data={props.leadsSourceChannels}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <ControlledSelect
                            control={control}
                            label={t("leads.form.caretaker")}
                            name="leadCaretakerId"
                            data={props.sellers}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <LeadStatusSelect
                            getValues={getValues}
                            initialStatus={props.defaultValues?.leadStatus}
                            initialStatusReason={props.defaultValues?.statusReason}
                            setValue={setValue}
                            label={`${t("leads.form.status")}*`}
                            leadsStatuses={props.leadsStatuses}
                            failureReasons={props.failureReasons}
                        />
                    </Grid>
                    {watch('leadStatus') === "Failure" && <Grid item xs={12}>
                        <ControlledTextField
                            control={control}
                            name="statusNote"
                            label={`${t("leads.form.statusNote")}`}
                            props={{
                                rows: 3,
                                multiline: true,
                                sx: { marginBottom: 2 },
                                inputProps: { style: { resize: "vertical" } },
                            }}
                        />
                    </Grid>}
                    <Grid item xs={12}>
                        <ControlledSelect
                            control={control}
                            name="leadInvestment"
                            label={`${t("leads.form.investment")}`}
                            data={props.investments}
                        />
                    </Grid>
                </Grid>
                <Grid item xs={12} sx={{ mt: 4 }}>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={!submitEnabled || props.isLoading}
                        onClick={handleSubmit(props.onSubmit, props.onError)}
                        loading={props.isLoading}
                    >
                        {props.submitBtnText}
                    </LoadingButton>
                </Grid>
            </FormControl>
        </Grid>
    );
}
